import React, { useEffect, useState, useCallback } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import "../Users/Users.scss";
import profileDefault from "../../assets/images/profileDefault.png";

// Discover invite tracking: who has invited people to ZONA via the in-app
// Discover invite flow, and the numbers they invited. Backed by the admin-
// only GET /users/admin/discover-invites endpoint. Invitees are stored only
// as phone numbers (the invite is an SMS share), so a number is tagged with a
// name only when it matches an existing ZONA user. Mirrors the DeletedUsers
// page pattern (self-contained dark table, no shared TableColumn).
const DiscoverInvites = () => {
  const [inviters, setInviters] = useState([]);
  const [summary, setSummary] = useState({
    total_inviters: 0,
    total_invites: 0,
    unique_invited: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/users/admin/discover-invites`);
      setInviters(data?.data || []);
      setSummary({
        total_inviters: data?.total_inviters || 0,
        total_invites: data?.total_invites || 0,
        unique_invited: data?.unique_invited || 0,
      });
    } catch (err) {
      console.error("Failed to fetch discover invites:", err);
      setInviters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const filtered = inviters.filter((u) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.phone || "").toLowerCase().includes(s) ||
      (u.invited || []).some((i) => (i.number || "").includes(s))
    );
  });

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ width: "100%", marginBottom: 16 }}>
            <h1 style={{ margin: 0, color: "#fff" }}>Invites</h1>
            <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
              Who has invited people to ZONA via Discover, and the numbers they invited.
              A number shows a name only when it matches an existing ZONA user.
            </p>
          </div>

          {/* Stat cards */}
          <div style={{ width: "100%", display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            {[
              { num: summary.total_inviters, label: "Inviters" },
              { num: summary.total_invites, label: "Invites sent" },
              { num: summary.unique_invited, label: "Unique people invited" },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 12,
                  padding: "16px 20px",
                  minWidth: 170,
                }}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color: "#4F30EA", lineHeight: 1.1 }}>
                  {s.num}
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ width: "100%", marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search inviter name, email, or number…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 13,
                outline: "none",
                width: 300,
              }}
            />
          </div>

          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              // Scroll a wide table inside its own box instead of letting the
              // .page-content flex container (align-items:center) center-
              // overflow it under the fixed sidebar. See DeletedUsers.
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 720 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>Inviter</th>
                  <th style={th}>Email</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Invites</th>
                  <th style={th}>Invited numbers</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No invites yet. This fills in once people start inviting from Discover.
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr
                      key={u.firebase_id || u._id}
                      style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
                    >
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={u.photo_url || profileDefault}
                            alt=""
                            style={{ width: 32, height: 32, borderRadius: 16, objectFit: "cover", background: "#222" }}
                          />
                          <span>{u.name || "—"}</span>
                        </div>
                      </td>
                      <td style={td}>{u.email || "—"}</td>
                      <td style={td}>{u.phone || "—"}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{u.invite_count}</td>
                      <td
                        style={{
                          ...td,
                          maxWidth: 360,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 90,
                          overflowY: "auto",
                        }}
                      >
                        {(u.invited || [])
                          .map((i) => (i.existing_user ? `${i.number} (${i.existing_user})` : i.number))
                          .join(", ") || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </main>
  );
};

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = {
  padding: "12px 14px",
  fontSize: 13,
  verticalAlign: "middle",
};

export default DiscoverInvites;
